import { action, computed, makeObservable } from 'mobx';

import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { BoardRef, Favorites, QueueRef, Task } from '@/tracker/types';

import { isBoolean, StoredValueModel } from './StoredValueModel';

const RECENT_LIMIT = 10;

/** Недавно открытое: id — ключ задачи или очереди, id доски */
export type RecentItem = { type: 'issue' | 'queue' | 'board'; id: string; name: string };

const isFavorites = (saved: unknown): saved is Favorites =>
  Array.isArray((saved as Favorites | null)?.boards) &&
  Array.isArray((saved as Favorites | null)?.queues);

const isRecent = (saved: unknown): saved is RecentItem[] => Array.isArray(saved);

/**
 * Общее состояние вкладки «Трекер»: оповещения об изменённых задачах, избранное, недавние и вид навигации.
 * Всё, кроме оповещений, помнит этот браузер
 */
export class TrackerStore extends LocalStore {
  private readonly _updatedIssue = new ValueModel<Task | null>(null);
  // shortcut: избранное и недавние в localStorage — общие для всех, кто входит в этом браузере.
  // Когда у бэкенда появится `/users/me/favorites` (форма та же — Favorites), читать и писать туда
  private readonly _favorites = new StoredValueModel<Favorites>({
    key: 'tracker.favorites',
    fallback: { boards: [], queues: [] },
    isValid: isFavorites,
  });
  private readonly _recent = new StoredValueModel<RecentItem[]>({
    key: 'tracker.recent',
    fallback: [],
    isValid: isRecent,
  });
  private readonly _isNavCollapsed = new StoredValueModel({
    key: 'tracker.navCollapsed',
    fallback: false,
    isValid: isBoolean,
  });
  /** Раскрыт ли раздел навигации; модели заводятся при первом обращении */
  private readonly _navSections = new Map<string, StoredValueModel<boolean>>();

  constructor() {
    super();
    makeObservable(this, {
      updatedIssue: computed,
      favoriteBoards: computed,
      favoriteQueues: computed,
      recent: computed,
      isNavCollapsed: computed,
      notifyIssueUpdated: action.bound,
      addRecent: action.bound,
      forgetBoard: action.bound,
      toggleFavoriteBoard: action.bound,
      toggleFavoriteQueue: action.bound,
      toggleNavCollapsed: action.bound,
      toggleNavSection: action.bound,
    });
  }

  /** Последняя изменённая в карточке задача — списки подхватывают её через reaction */
  get updatedIssue() {
    return this._updatedIssue.value;
  }

  /** В порядке добавления */
  get favoriteBoards() {
    return this._favorites.value.boards;
  }

  get favoriteQueues() {
    return this._favorites.value.queues;
  }

  /** Свежие сверху */
  get recent() {
    return this._recent.value;
  }

  get isNavCollapsed() {
    return this._isNavCollapsed.value;
  }

  /** По умолчанию разделы раскрыты */
  isNavSectionOpen(id: string) {
    return this.navSection(id).value;
  }

  notifyIssueUpdated(issue: Task) {
    this._updatedIssue.change(issue);
  }

  isFavoriteBoard(id: string) {
    return this.favoriteBoards.some((board) => board.id === id);
  }

  isFavoriteQueue(key: string) {
    return this.favoriteQueues.some((queue) => queue.key === key);
  }

  toggleFavoriteBoard({ id, name }: BoardRef) {
    const boards = this.isFavoriteBoard(id)
      ? this.favoriteBoards.filter((board) => board.id !== id)
      : [...this.favoriteBoards, { id, name }];

    this._favorites.change({ ...this._favorites.value, boards });
  }

  toggleFavoriteQueue({ key, name }: QueueRef) {
    const queues = this.isFavoriteQueue(key)
      ? this.favoriteQueues.filter((queue) => queue.key !== key)
      : [...this.favoriteQueues, { key, name }];

    this._favorites.change({ ...this._favorites.value, queues });
  }

  /** Открыли задачу, очередь или доску: поднимаем наверх со свежим названием */
  addRecent(item: RecentItem) {
    const rest = this.recent.filter(({ type, id }) => type !== item.type || id !== item.id);
    const recent = [item, ...rest].slice(0, RECENT_LIMIT);

    this._recent.change(recent);
  }

  /** Доску удалили — убираем из избранного и недавних */
  forgetBoard(id: string) {
    if (this.isFavoriteBoard(id)) this.toggleFavoriteBoard({ id, name: '' });

    this._recent.change(this.recent.filter((item) => item.type !== 'board' || item.id !== id));
  }

  toggleNavCollapsed() {
    this._isNavCollapsed.change(!this.isNavCollapsed);
  }

  toggleNavSection(id: string) {
    const section = this.navSection(id);

    section.change(!section.value);
  }

  private navSection(id: string) {
    const existing = this._navSections.get(id);

    if (existing) return existing;

    const section = new StoredValueModel({
      key: `tracker.navSection.${id}`,
      fallback: true,
      isValid: isBoolean,
    });

    this._navSections.set(id, section);

    return section;
  }
}
